import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import ImageSet from "../../../components/image-set";
import ReviewItem from "../../../components/review-item";
import PhotoListItem from "../../../components/photo-list-item";
import Tags from "../../../components/tags";
import Questions from "../../../components/questions";
import Accordion from "../../../components/accordion";
import Warning from "../../../components/warning";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

import InputCalculator from "../components/input-calculator";

import CompatibilityModule from "./compatibilityModule";

const headerInfo = lookupByUrl(
  namedRoutes.ecoflow.altCharger.gen2Compatibility
);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      short
      positionY={27}
      background="/images/backgrounds/ecoflow-gen-2-alternator-chargers.webp?v=2"
      titlePrefix="EcoFlow Alternator Charger"
      title="Power Station Compatibility Guide"
      subTitle="600W & Plus 1000W Models"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          This guide provides the most in-depth compatibility data for the{" "}
          <strong>EcoFlow Alternator Charger 600</strong> and{" "}
          <strong>Plus 1000</strong> DC-DC chargers, including performance with{" "}
          <strong>EcoFlow power stations</strong> and{" "}
          <strong>third‑party brands</strong> like Jackery, Anker, and BLUETTI.
          Use the <strong>compatibility calculator</strong> below to estimate
          the expected charging performance for your setup.
        </p>

        <AffiliateDisclosure />

        <p>For the full details of both models, see my review:</p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />

        <p class="caption-left">
          <strong>Note:</strong> These models are currently only available in
          select international regions and are not expected to launch in North
          America in 2026. See{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen2}>my review</Link> for
          more information.
        </p>
      </Center>
      <Center>
        <h2>EcoFlow Power Station Compatibility</h2>
        <p>
          The tables below outline <strong>compatibility</strong>, supported{" "}
          <strong>input limits</strong>, and <strong>cable requirements</strong>{" "}
          when paired with an EcoFlow power station using <strong>XT60</strong>,{" "}
          <strong>XT150</strong>, or <strong>4+8</strong> (Custom Fit) output
          cables. If you are unsure which ports your unit has, check out my{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen2CableGuide}>
            connection guide
          </Link>{" "}
          for a visual breakdown of the ports on all compatible EcoFlow power
          stations.
        </p>

        {/* <ImageSet
          size={6}
          showAlt
          images={[
            {
              lg: "/images/products/ecoflow/assorted/ports/xt60.webp",
              alt: "XT60 Solar Port",
            },
            {
              lg: "/images/products/ecoflow/assorted/ports/xt150.webp",
              alt: "XT150 Expansion Port",
            },
            {
              lg: "/images/products/ecoflow/assorted/ports/4+8.webp",
              alt: "4+8 Expansion Port",
            },
          ]}
        /> */}

        <p class="caption-left">
          Highlights denote the cable included with each charger model (
          <mark class={style.xt60}>
            <strong>XT60</strong>
          </mark>
          ,{" "}
          <mark class={style.xt150}>
            <strong>XT150</strong>
          </mark>
          ). For other cable options and adapter requirements, please refer to
          the numbered footnotes.
        </p>

        <CompatibilityModule />

        <p>
          <strong>Reverse charging</strong> and{" "}
          <strong>battery maintenance modes</strong> require a compatible
          EcoFlow DELTA series model and either an XT150 or 4+8 output cable.
          These features are not supported while using the XT60 output cable or
          any third‑party power station. For a full breakdown of how these modes
          work, see my{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen2}>
            detailed full review
          </Link>
          .
        </p>
        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />
      </Center>
      <Center>
        <h2>Third-Party Power Station Compatibility</h2>
        <p>
          Want to use an EcoFlow Alternator Charger with your BLUETTI, Jackery,
          Anker or other brand solar generator? Use my{" "}
          <strong>compatibility calculator</strong> to{" "}
          <strong>verify support</strong> and see{" "}
          <strong>expected charging power</strong>. If your solar input port is
          already in use or needs an <strong>XT60</strong> adapter, you'll find
          solutions in the sections below.
        </p>

        <InputCalculator
          minChargingVolts={11}
          highestMinChargingVolts={15}
          lowestMaxChargingVolts={24}
          maxChargingVolts={60}
          minChargingAmps={0}
          maxChargingAmps={12}
          chargerProfiles={{
            "Alternator Charger 600": {
              maxWatts: 600,
            },
            "Alternator Charger Plus 1000": {
              maxWatts: 720,
            },
          }}
          highWarning={15}
        />

        <Warning prefix="Important" icon={false}>
          Charging third-party power stations{" "}
          <strong>requires the XT60 output cable</strong>. This cable is
          included with the Alternator Charger 600, but must be purchased
          separately for the Plus 1000 model.
        </Warning>

        <p>
          Actual output may be lower depending on your setup. Alternator size,
          battery voltage (12V vs 24V), vehicle battery health, and ambient
          temperature can all affect performance. Your power station’s minimum
          start‑up voltage may also limit the charging rate.
        </p>

        <p>
          <strong>More details:</strong> My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers alternator load, smart alternators, idling vs driving, and
          real‑world charging performance. It also compares chargers across all
          major brands so you can choose the setup that works best for you.
        </p>
        <ReviewItem href={namedRoutes.alternatorChargers} />

        <h3>XT60 port already in use?</h3>
        <p>
          If you only have one XT60 port and already use it for solar panels,
          you have two options:
        </p>
        <ol>
          <li>
            <strong>Use the Alternator Charger Plus 1000:</strong> This model
            includes a built‑in solar controller, allowing your solar panels and
            the alternator charger to operate at the same time through separate
            inputs.
            <ul>
              <li>
                <strong>Solar Specs:</strong> The solar input on the Plus 1000
                accepts 11-60V⎓15A (300W max)
              </li>
              <li>
                <strong>Accessories Required:</strong>{" "}
                <span>EcoFlow Solar Input Cable</span> and{" "}
                <span>EcoFlow XT60 Output Cable</span>.
              </li>
            </ul>
          </li>
          <li>
            <strong>Manual Swapping:</strong> Manually unplug your solar panels
            and plug in the alternator charger when driving.
            <Warning prefix="Pro Tip" blue icon={false}>
              To prevent wearing out your power station’s internal port from
              frequent unplugging, use a short{" "}
              <Link
                affiliate="https://amzn.to/4nzlm6E"
                href="https://www.amazon.com/dp/B0DPQFWDKW"
              >
                XT60 extension cord
              </Link>
              .
            </Warning>
          </li>
        </ol>

        <h3>Don't have an XT60 port?</h3>
        <p>
          While XT60 is the standard for most modern portable power station and
          solar generators, some brands or older models use different
          connectors. If your unit doesn’t have an XT60 port, you can usually
          use an adapter for the connection. You will need an XT60 male adapter
          rated for at least <strong>12A</strong> to connect to the alternator
          charger.
        </p>

        <PhotoListItem
          img="/images/products/xt60-male.webp"
          title="XT60 Male"
          titleSuffix="(pins are male)"
        />
        {/* <p>
          To ensure safe and efficient charging, use a short, high-quality
          adapter rated for at least <mark>12A</mark> continuous current.
        </p> */}

        {/* <ul>
          <li>
            <strong>
              <mark>
                <Link
                  affiliate="https://amzn.to/4r4LtmB"
                  href="https://www.amazon.com/dp/B0BBQSTVVH/"
                >
                  MC4
                </Link>
              </mark>
              :
            </strong>{" "}
            Common for many brands.
          </li>
          <li>
            <strong>
              <Link
                affiliate="https://amzn.to/4jNwfQt"
                href="https://www.amazon.com/dp/B0DFC7V9CR/"
              >
                Anderson Powerpole
              </Link>
              :
            </strong>{" "}
            Common for....
          </li>
          <li>
            <strong>
              <mark>8mm (DC7909)</mark>:
            </strong>{" "}
            Common for....
          </li>
          <li>
            <strong>
              <mark>5.5mm x 2.1mm (DC5521)</mark>:
            </strong>{" "}
            Common for....
          </li>
          <li>
            <strong>
              <Link
                affiliate="https://amzn.to/463Y69l"
                href="https://www.amazon.com/dp/B0DPQFC4PR/"
              >
                XT60 (male) to XT60 (male)
              </Link>
              :
            </strong>{" "}
            Can be used to connect other adapters.
          </li>
        </ul> */}

        <Warning prefix="Pro Tip" blue icon={false}>
          Use a short XT60 adapter. Long or thin‑gauge cables can cause voltage
          drop, which may impact charging performance or prevent charging from
          starting on some power stations that have a high minimum voltage.
        </Warning>

        <Accordion
          headingsAs="h2"
          items={[
            {
              heading: "Frequently asked questions (FAQ)",
              content: (
                <Fragment>
                  <div class={style.faq}>
                    <div>
                      <h3>
                        Are EcoFlow Alternator Chargers compatible with
                        third-party power stations?
                      </h3>
                      <p>
                        <strong>
                          Yes, EcoFlow Alternator Chargers are compatible with
                          many third-party power stations (such as Jackery,
                          BLUETTI, and Anker) via the XT60 output cable.
                        </strong>{" "}
                        This cable is included with 500W and 600W models but
                        sold separately for the 800W and Plus 1000. If your unit
                        uses a different connector such as Anderson, 8mm, or MC4
                        ports, you can connect using a high-quality XT60
                        adapter. Ensure your power station’s solar input voltage
                        range is compatible with the charger's output. Also note
                        that "Reverse Charge" and "Battery Maintenance" modes
                        are exclusive to compatible EcoFlow DELTA series units
                        using XT150 or 4+8 cables.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Can I use solar and alternator charging at the same
                        time?
                      </h3>
                      <p>
                        <strong>
                          Yes, you can use solar and alternator charging at the
                          same time as long as your solar generator supports
                          multiple DC inputs or you’re using the Plus 1000
                          model.
                        </strong>{" "}
                        Many EcoFlow and third‑party units can combine both
                        sources through separate solar ports, while the Plus
                        1000 adds its own 11–60V solar input (300W max) for
                        simultaneous charging without needing an extra port.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Does the Plus 1000 charge faster than the 600W model
                        when using an XT60 cable?
                      </h3>
                      <p>
                        <strong>
                          Yes, the Plus 1000 has a slightly higher maximum
                          output of 720W when using the XT60 cable, compared to
                          the 600W limit of the standard model.
                        </strong>{" "}
                        However, your actual charging speed is also impacted by
                        your power station’s input specifications. If your
                        station is limited to 600W or approximately 10A, both
                        chargers will perform identically. To unlock the Plus
                        1000’s full 1000W potential, you must use the XT150 or
                        4+8 output cable with a compatible EcoFlow DELTA series
                        unit.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Why am I not reaching the full 600W or 1000W charging
                        speed?
                      </h3>
                      <p>
                        <strong>
                          Maximum charging speeds are limited by your portable
                          power station’s input specifications, the output cable
                          used, and additional factors such as alternator size,
                          vehicle battery health, and high ambient temperatures.
                        </strong>{" "}
                        To reach full speeds, ensure you are using an XT150 or
                        4+8 cable with a compatible high-input EcoFlow power
                        station and a healthy vehicle electrical system capable
                        of sustained high-current output.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Can I use the "Reverse Charge" feature with a
                        non-EcoFlow power station?
                      </h3>
                      <p>
                        <strong>
                          No, the "Reverse Charge" and "Battery Maintenance"
                          features are only supported with a compatible EcoFlow
                          DELTA series unit while using the XT150 or 4+8 output
                          cable.
                        </strong>{" "}
                        Most power stations' solar input ports are physically
                        incapable of exporting power to the charger. Therefore,
                        EcoFlow restricts these features to the XT150 or 4+8
                        output cables.
                      </p>
                    </div>
                    <div>
                      <h3>
                        What happens if my portable power station has a maximum
                        input voltage below 24V?
                      </h3>
                      <p>
                        <strong>
                          Connecting a portable power station with a maximum
                          input voltage below 24V is incompatible and poses a
                          risk of hardware damage.
                        </strong>{" "}
                        If the charger’s output voltage exceeds your power
                        station's input limit, it could trigger an over-voltage
                        protection (OVP) error or, in units lacking protective
                        circuitry, permanently damage the internal charge
                        controller. Always verify your power station’s "Max
                        Volts" specification and use the EcoFlow app to set a
                        custom max voltage limit between 24V and 60V.
                      </p>
                    </div>
                  </div>
                </Fragment>
              ),
            },
            // {
            //   heading: "Popular third-party combinations",
            //   content: <Fragment></Fragment>,
            // },
          ]}
        />
      </Center>
      <Center>
        <h2>Vehicle Compatibility</h2>
        <p>
          Both the 600W and Plus 1000 models accept a wide input range of
          11V–31V, making them compatible with standard 12V (default) passenger
          vehicles and 24V systems found in heavy-duty trucks or RV house
          batteries.
        </p>
        <ul>
          <li>
            <strong>Automatic Voltage Detection:</strong> The chargers feature
            automatic voltage detection to determine when to start charging.
            While they have a preset threshold, it is highly recommended to
            manually configure the start-voltage value in the app to match your
            specific vehicle. This ensures charging begins when the engine
            starts and stops reliably when the engine is off, to prevent
            draining your starter battery.
          </li>
          <li>
            <strong>Smart Alternator Support:</strong> The new 600W and Plus
            1000 models include an optional ignition sensing wire that improves
            reliability for modern vehicles with smart alternators. This
            provides a physical signal to the charger that the engine is
            running, which is more reliable than relying on voltage fluctuations
            alone.
          </li>
        </ul>
      </Center>
      <Center>
        <h2>Cables and Accessories</h2>
        <h3>Output Cables</h3>
        <p>
          Cable type determines system compatibility, available features, and
          maximum power output. Ideally you would be able to select which cable
          you want included when purchasing, but that's not the case.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output"
              img="/images/products/ecoflow/alternator-charger-600/xt60-cable.webp"
              title="XT60 output cable"
              titleSuffix="(EcoFlow or Third-Party / Japan Link)"
              description={
                <Fragment>
                  Included in: 600W model (14AWG, 1m)
                  <br />
                  Supports: charging (720W maximum)
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt150output-new-1m"
              img="/images/products/ecoflow/alternator-charger-plus-1000/xt150-cable.webp"
              title="XT150 output cable"
              titleSuffix="(EcoFlow only / Japan Link)"
              description={
                <Fragment>
                  Included in: 1000W model (12AWG, 1m)
                  <br />
                  Supports: charging (full rate), reverse charging, battery
                  maintenance
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
              img="/images/products/ecoflow/accessories/output-cable-4+8.webp"
              title="4+8 output cable"
              titleSuffix="(EcoFlow only / Japan Link)"
              description={
                <Fragment>
                  Sold separately
                  <br />
                  Supports: charging (full rate), reverse charging, battery
                  maintenance
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <h3>Accessories</h3>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://au.ecoflow.com/products/ecoflow-alternator-charger-plus-1000-solar-input-cable"
              img="/images/products/ecoflow/alternator-charger-plus-1000/solar-cable.webp"
              title="Plus 1000 Solar Input Cable"
              titleSuffix="(Australia link)"
              description={
                <Fragment>
                  Sold separately
                  <br />
                  16.4ft (5m), 12AWG
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
              img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
              title="Smart Generator Adapter"
              description={
                <Fragment>
                  Required by some power stations when using XT150 output cable
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
              img="/images/products/ecoflow/accessories/extra-battery-expansion-adapter.webp"
              title="Extra Battery Expansion Adapter"
              description={
                <Fragment>
                  Connects an alternator charger and extra battery for the DELTA
                  3 Max Plus and Ultra Plus
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
              img="/images/products/ecoflow/accessories/extended-fuse-cable-2m.webp"
              title="Extended Fuse Cable"
              description={<Fragment>Length: 2m</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>
      </Center>
      <Center>
        <h2>EcoFlow Alternator Charger Reviews</h2>
        <p>
          Explore all EcoFlow alternator chargers models. Learn about
          differences, features, reverse charging, and more:
        </p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2CableGuide} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />
          </li>
          <li>
            <ReviewItem
              href={namedRoutes.ecoflow.altCharger.gen1Compatibility}
            />
          </li>
        </ul>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
    </Editorial>
  </div>
);

export default Page;
